import { moderateImage } from "@/lib/imageModeration";
import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ScrollTimePicker from "@/components/admin_dashboard/ScrollTimePicker";
import BulkCreateSlotsModal from "@/components/admin_dashboard/BulkCreateSlotsModal";
import DuplicateDaySlotsModal from "@/components/admin_dashboard/DuplicateDaySlotsModal";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import AdminTabActionBar from "@/components/admin_dashboard/AdminTabActionBar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Loader2, Trash2, X, CalendarDays, Check, TrendingUp, Eye, MoreHorizontal, Zap, Car, TrainFront, Search, SlidersHorizontal, ImageIcon, Upload, RefreshCw, Copy } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { plans } from "@/config/stripePlans";
import { APPOINTMENT_BOOKING_ENABLED } from "@/config/featureFlags";
import { ImageCropDialog } from "@/components/admin_dashboard/ImageCropDialog";
const MAX_LISTING_IMAGES = 5;

interface Listing {
  listing_id: string;
  name: string;
  description: string | null;
  duration: string;
  spots: number;
  price: number;
  time: string;
  images?: string[];
  schedule: string | null;
  terms_and_conditions: string | null;
  sort_order: number;
  is_active: boolean;
  outlet_id: string;
  booking_mode?: string | null;
  default_billing_mode?: string | null;
  teacher_name?: string | null;
  collect_attendee?: boolean;
  duration_minutes?: number | null;
  buffer_minutes?: number | null;
  booking_cutoff_hours?: number | null;
  outlets?: { outlet_name: string };
  faqs?: { question: string; answer: string }[];
  custom_questions?: { id: string; question: string; type: "text" | "dropdown" | "radio"; required: boolean; options?: string[] }[];
}

interface WeeklySchedule {
  [day: string]: string[];
}

interface DateException {
  date: string;
  slots: string[];
  isClosed: boolean;
}

interface ScheduleData {
  type?: 'recurring' | 'one_off';
  weekly: WeeklySchedule;
  exceptions: DateException[];
}

const DAYS_OF_WEEK = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS: Record<string, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const DEFAULT_WEEKLY: WeeklySchedule = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};

const HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => i); // 0-12 hours
const MINUTE_OPTIONS = [0, 15, 30, 45];

const parseDuration = (duration: string): { hours: number; minutes: number } => {
  const hourMatch = duration.match(/(\d+)\s*hr/i);
  const minMatch = duration.match(/(\d+)\s*min/i);
  return {
    hours: hourMatch ? parseInt(hourMatch[1]) : 0,
    minutes: minMatch ? parseInt(minMatch[1]) : 0,
  };
};

const formatDuration = (hours: number, minutes: number): string => {
  const parts = [];
  if (hours > 0) parts.push(`${hours} hr`);
  if (minutes > 0) parts.push(`${minutes} min`);
  return parts.length > 0 ? parts.join(" ") : "0 min";
};

const formatTimeTo12h = (time24: string): string => {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr) || 0;
  const m = mStr || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
};

// Single-line cell text that truncates and shows the full value in a tooltip on hover.
function CellText({ children }: { children: React.ReactNode }) {
  if (children == null || children === "") return <span>—</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block truncate">{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[320px] whitespace-pre-wrap break-words text-xs">{children}</TooltipContent>
    </Tooltip>
  );
}

const StorefrontListings = ({ onNavigateToPricing }: { onNavigateToPricing?: () => void }) => {
  const { businessId } = useAuth();
  const queryClient = useQueryClient();
  const { outlets, activeOutlet, loading: outletLoading } = useOutlet();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingListing, setEditingListing] = useState<Listing | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState<Listing | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [listingToDeactivate, setListingToDeactivate] = useState<Listing | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [priceChangeModalOpen, setPriceChangeModalOpen] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState<React.FormEvent | null>(null);
  const navigate = useNavigate();

  const { subscriptionTier } = useSubscription();
  const currentPlan = (subscriptionTier || "free").toLowerCase();
  const plan = plans[currentPlan];
  const listingLimit = plan?.limits?.listings ?? null;

  const { data: totalListingsCount = 0, refetch: refetchTotalListings } = useQuery({
    queryKey: ['totalListingsCount', businessId],
    queryFn: async () => {
      if (!businessId) return 0;
      const { count } = await supabase
        .from("listings")
        .select("*", { count: "exact", head: true })
        .eq("business_id", businessId);
      return count || 0;
    },
    enabled: !!businessId,
  });

  const [listingFilters, setListingFilters] = useState<{
    activeStatus: "all" | "active" | "inactive";
    priceMin: string;
    priceMax: string;
    duration: "all" | "under_30" | "30_60" | "60_120" | "120_plus";
  }>({
    activeStatus: "all",
    priceMin: "",
    priceMax: "",
    duration: "all",
  });
  const [listingFilterOpen, setListingFilterOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    teacher_name: "",
    duration: "",
    spots: 10,
    booking_mode: "session" as "session" | "appointment",
    default_billing_mode: "upfront" as "upfront" | "per_session",
    collect_attendee: false,
    buffer_minutes: 0,
    booking_cutoff_hours: null as number | null,
    price: 0,
    terms_and_conditions: "",
    is_active: true,
    tags: [] as string[],
    outlet_id: "",
    images: [] as string[],
    faqs: [] as { question: string; answer: string }[],
    custom_questions: [] as { id: string; question: string; type: "text" | "dropdown" | "radio"; required: boolean; options?: string[] }[],
  });
  const [formStep, setFormStep] = useState<1 | 2 | 3>(1);
  const [uploadingImageIndex, setUploadingImageIndex] = useState<number | null>(null);
  // Photo selected but not yet cropped: opens the crop/resize dialog before upload.
  const [pendingCrop, setPendingCrop] = useState<{ file: File; index: number } | null>(null);
  const imageInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleData>({
    weekly: { ...DEFAULT_WEEKLY },
    exceptions: [],
  });
  const [newTimeSlot, setNewTimeSlot] = useState<Record<string, string>>({});
  const [exceptionDate, setExceptionDate] = useState<Date | undefined>();
  const [exceptionSlots, setExceptionSlots] = useState<string[]>([]);

  const [newExceptionSlot, setNewExceptionSlot] = useState("");
  const [timePickerDay, setTimePickerDay] = useState<string | null>(null);
  const [exceptionTimePickerOpen, setExceptionTimePickerOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const { data: listings = [], isLoading: queryLoading } = useQuery({
    queryKey: ['listings', businessId, activeOutlet?.outlet_id],
    queryFn: async () => {
      if (!businessId || !activeOutlet) return [];

      const { data, error } = await supabase
        .from("listings")
        .select("*, outlets(outlet_name)")
        .eq("business_id", businessId)
        .eq("outlet_id", activeOutlet.outlet_id)
        .order("sort_order");

      if (error) {
        toast.error("Failed to fetch listings");
        throw error;
      }
      return (data as unknown as Listing[]) || [];
    },
    enabled: !!businessId && !outletLoading && !!activeOutlet,
    staleTime: 30000,
  });

  useEffect(() => {
    if (activeOutlet && !formData.outlet_id) {
      setFormData(prev => ({ ...prev, outlet_id: activeOutlet.outlet_id }));
    }
  }, [activeOutlet, formData.outlet_id]);

  const parseScheduleData = (schedule: any): ScheduleData => {
    if (!schedule || typeof schedule !== 'object') {
      return { type: 'recurring', weekly: { ...DEFAULT_WEEKLY }, exceptions: [] };
    }
    return {
      type: schedule.type || 'recurring',
      weekly: schedule.weekly || { ...DEFAULT_WEEKLY },
      exceptions: schedule.exceptions || [],
    };
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      teacher_name: "",
      duration: "",
      spots: 10,
      booking_mode: "session",
      default_billing_mode: "upfront",
      collect_attendee: false,
      buffer_minutes: 0,
      booking_cutoff_hours: null,
      price: 0,
      terms_and_conditions: "",
      is_active: true,
      tags: [],
      outlet_id: activeOutlet?.outlet_id || "",
      images: [],
      faqs: [],
      custom_questions: [],
    });
    setFormStep(1);
    setScheduleData({ type: 'recurring', weekly: { ...DEFAULT_WEEKLY }, exceptions: [] });
    setNewTimeSlot({});
    setExceptionDate(undefined);
    setExceptionSlots([]);
    setNewExceptionSlot("");
    setEditingListing(null);
    setUploadingImageIndex(null);
  };

  const handleEdit = (listing: Listing) => {
    setEditingListing(listing);
    setFormData({
      name: listing.name,
      description: listing.description || "",
      teacher_name: (listing as any).teacher_name || "",
      // Appointment listings drive duration off duration_minutes; surface that
      // in the shared numeric duration field so the editor round-trips cleanly.
      duration: listing.booking_mode === "appointment"
        ? (listing.duration_minutes ? `${listing.duration_minutes} min` : "")
        : listing.duration,
      spots: listing.spots,
      booking_mode: (listing.booking_mode === "appointment" ? "appointment" : "session"),
      default_billing_mode: (listing.default_billing_mode === "per_session" ? "per_session" : "upfront"),
      collect_attendee: (listing as any).collect_attendee ?? false,
      buffer_minutes: listing.buffer_minutes ?? 0,
      booking_cutoff_hours: listing.booking_cutoff_hours ?? null,
      price: listing.price,
      terms_and_conditions: listing.terms_and_conditions || "",
      is_active: listing.is_active,
      tags: (listing as any).tags || [],
      outlet_id: listing.outlet_id,
      images: listing.images || [],
      faqs: listing.faqs || [],
      custom_questions: listing.custom_questions || [],
    });
    setFormStep(1);
    setScheduleData(parseScheduleData(listing.schedule));
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (listing: Listing) => {
    const { error } = await supabase
      .from("listings")
      .update({ is_active: !listing.is_active })
      .eq("listing_id", listing.listing_id);

    if (error) {
      toast.error("Failed to update listing");
    } else {
      queryClient.invalidateQueries({ queryKey: ['listings', businessId, activeOutlet?.outlet_id] });
    }

    // Close modal if it was used
    setDeactivateModalOpen(false);
    setListingToDeactivate(null);
  };

  const handleToggleClick = (listing: Listing) => {
    if (listing.is_active) {
      // Trying to make it inactive, show modal
      setListingToDeactivate(listing);
      setDeactivateModalOpen(true);
    } else {
      // Trying to make it active, do it immediately
      handleToggleActive(listing);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const hasTimeSlots = scheduleData.type === 'one_off'
      ? scheduleData.exceptions.some(e => e.slots.length > 0)
      : Object.values(scheduleData.weekly).some(slots => slots.length > 0);
    if (!hasTimeSlots) {
      toast.error("At least one timeslot for this listing should be created", {
        position: "bottom-right"
      });
      return;
    }

    // Check if price changed during edit and warn the business
    if (editingListing && formData.price !== editingListing.price) {
      setPendingSubmitEvent(e);
      setPriceChangeModalOpen(true);
      return;
    }

    await executeSubmit();
  };

  const executeSubmit = async () => {

    const firstTimeSlot = scheduleData.type === 'one_off'
      ? (scheduleData.exceptions.find(e => e.slots.length > 0)?.slots[0] || "TBD")
      : (Object.values(scheduleData.weekly).flat()[0] || "TBD");
    const durationStr = formData.duration || "0 min";
    // While the class-based vertical is active, appointment mode is hidden and
    // every listing is forced to 'session', even if we're editing a legacy
    // appointment listing whose form still carries booking_mode='appointment'.
    const isAppointment = APPOINTMENT_BOOKING_ENABLED && formData.booking_mode === "appointment";
    // Appointment availability is computed from the service's minute-length, so
    // duration_minutes is the source of truth; it's parsed from the same numeric
    // duration field. Session listings keep duration_minutes null (grid-driven).
    const durationMin = parseInt(formData.duration.replace(/[^0-9]/g, ""), 10);

    const submitData = {
      name: formData.name,
      description: formData.description || null,
      teacher_name: formData.teacher_name?.trim() || null,
      duration: durationStr,
      spots: formData.spots,
      price: formData.price,
      time: firstTimeSlot,
      schedule: scheduleData,
      booking_mode: isAppointment ? "appointment" : "session",
      default_billing_mode: formData.default_billing_mode,
      collect_attendee: formData.collect_attendee,
      duration_minutes: isAppointment ? (Number.isFinite(durationMin) && durationMin > 0 ? durationMin : null) : null,
      buffer_minutes: isAppointment ? (formData.buffer_minutes || 0) : 0,
      terms_and_conditions: formData.terms_and_conditions || null,
      is_active: formData.is_active,
      tags: formData.tags,
      images: formData.images,
      faqs: formData.faqs,
      custom_questions: formData.custom_questions,
      // Sent only when set or changed, so an untouched listing never names a
      // column the database might not have yet.
      ...(formData.booking_cutoff_hours !== (editingListing?.booking_cutoff_hours ?? null)
        ? { booking_cutoff_hours: formData.booking_cutoff_hours }
        : {}),
    };

    if (editingListing) {
      const { error } = await supabase
        .from("listings")
        .update(submitData as unknown as TablesUpdate<"listings">)
        .eq("listing_id", editingListing.listing_id);

      if (error) {
        toast.error("Failed to update listing");
      } else {
        toast.success("Listing updated");
        queryClient.invalidateQueries({ queryKey: ['listings', businessId, activeOutlet?.outlet_id] });
      }
    } else {
      if (!formData.outlet_id) {
        toast.error("Please select an outlet");
        return;
      }

      const { error } = await supabase.from("listings").insert({
        ...submitData,
        business_id: businessId!,
        outlet_id: formData.outlet_id,
        sort_order: listings.length,
      } as unknown as TablesInsert<"listings">);

      if (error) {
        toast.error("Failed to create listing");
      } else {
        toast.success("Listing created");
        queryClient.invalidateQueries({ queryKey: ['listings', businessId, activeOutlet?.outlet_id] });

        const { data: newCount } = await refetchTotalListings();
        if (listingLimit !== null && (newCount ?? 0) >= listingLimit) {
          setUpgradeModalOpen(true);
        }
      }
    }

    setIsDialogOpen(false);
    resetForm();
  };

  const handlePriceChangeConfirm = async () => {
    setPriceChangeModalOpen(false);
    setPendingSubmitEvent(null);
    await executeSubmit();
  };

  const handlePriceChangeCancel = () => {
    setPriceChangeModalOpen(false);
    setPendingSubmitEvent(null);
  };

  // Image upload handlers

  // Step 1: a file was picked. Validate it's an image, then open the crop
  // dialog so the operator frames it to the square cover shape before upload.
  const handleImageSelect = (file: File, index: number) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    setPendingCrop({ file, index });
  };

  // Step 2: crop confirmed → moderate + upload the framed, resized file.
  const handleCropComplete = (croppedFile: File) => {
    const target = pendingCrop;
    setPendingCrop(null);
    if (target) void handleImageUpload(croppedFile, target.index);
  };

  const handleImageUpload = async (file: File, index: number) => {
    if (!businessId) {
      toast.error("Business context is unavailable. Refresh and try again.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    setUploadingImageIndex(index);
    
    // Check for inappropriate content locally
    const moderation = await moderateImage(file);
    if (!moderation.safe) {
      toast.error(moderation.reason || "Image is not appropriate for upload.");
      setUploadingImageIndex(null);
      return;
    }

    const fileExt = file.name.split(".").pop();
    const filePath = `${businessId}/listings/${editingListing?.listing_id || "new"}/${Date.now()}_${index}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("business-assets")
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      toast.error("Failed to upload image");
      setUploadingImageIndex(null);
      return;
    }

    const { data: urlData } = supabase.storage
      .from("business-assets")
      .getPublicUrl(filePath);

    setFormData(prev => {
      const newImages = [...prev.images];
      if (index < newImages.length) {
        newImages[index] = urlData.publicUrl;
      } else {
        newImages.push(urlData.publicUrl);
      }
      return { ...prev, images: newImages };
    });
    setUploadingImageIndex(null);
    toast.success("Image uploaded");
  };

  const handleImageDelete = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const [activeBookingCount, setActiveBookingCount] = useState<number | null>(null);
  const [checkingBookings, setCheckingBookings] = useState(false);

  const checkActiveBookings = async (listingId: string): Promise<number> => {
    const { count, error } = await supabase
      .from("bookings")
      .select("*", { count: "exact", head: true })
      .eq("listing_id", listingId)
      .in("status", ["confirmed", "pending"]);

    if (error) {
      console.error("Failed to check active bookings:", error);
      return 0;
    }
    return count || 0;
  };

  const openDeleteModal = async (listing: Listing) => {
    setListingToDelete(listing);
    setDeleteModalOpen(true);
    setCheckingBookings(true);
    setActiveBookingCount(null);

    const count = await checkActiveBookings(listing.listing_id);
    setActiveBookingCount(count);
    setCheckingBookings(false);
  };

  const handleDelete = async (listingId?: string) => {
    const id = listingId || editingListing?.listing_id;
    if (!id || !businessId) return;

    // Hard block: check for active bookings before deleting
    setIsDeleting(true);
    const activeCount = await checkActiveBookings(id);
    if (activeCount > 0) {
      toast.error(
        `Cannot delete this listing — there ${activeCount === 1 ? "is" : "are"} ${activeCount} active booking${activeCount === 1 ? "" : "s"}. Please cancel or complete all bookings first.`,
        { duration: 5000 }
      );
      setIsDeleting(false);
      return;
    }

    const { error } = await supabase
      .from("listings")
      .delete()
      .eq("listing_id", id)
      .eq("business_id", businessId);

    if (error) {
      if (error.code === "23503") {
        toast.error("Cannot delete listing — it still has associated bookings. Please cancel or complete them first.");
      } else {
        toast.error("Failed to delete listing");
      }
    } else {
      queryClient.invalidateQueries({ queryKey: ['listings', businessId, activeOutlet?.outlet_id] });
      toast.success("Listing deleted successfully");
    }
    setIsDeleting(false);
    setDeleteModalOpen(false);
    setListingToDelete(null);
    setIsDialogOpen(false);
    resetForm();
  };

  const addTimeSlot = (day: string) => {
    const time = newTimeSlot[day];
    if (!time) return;

    if (scheduleData.weekly[day]?.includes(time)) {
      toast.error("Duplicate time slot for the same day is not allowed");
      return;
    }


    setScheduleData(prev => ({
      ...prev,
      weekly: {
        ...prev.weekly,
        [day]: [...(prev.weekly[day] || []), time].sort(),
      },
    }));
    setNewTimeSlot(prev => ({ ...prev, [day]: "" }));
  };

  const removeTimeSlot = (day: string, time: string) => {
    setScheduleData(prev => ({
      ...prev,
      weekly: {
        ...prev.weekly,
        [day]: prev.weekly[day].filter(t => t !== time),
      },
    }));
  };

  const addException = () => {
    if (!exceptionDate) return;
    const dateStr = format(exceptionDate, "yyyy-MM-dd");

    const existingIndex = scheduleData.exceptions.findIndex(e => e.date === dateStr);
    const newException: DateException = {
      date: dateStr,
      slots: exceptionSlots,
      isClosed: exceptionSlots.length === 0,
    };

    if (existingIndex >= 0) {
      setScheduleData(prev => ({
        ...prev,
        exceptions: prev.exceptions.map((e, i) => i === existingIndex ? newException : e),
      }));
    } else {
      setScheduleData(prev => ({
        ...prev,
        exceptions: [...prev.exceptions, newException].sort((a, b) => a.date.localeCompare(b.date)),
      }));
    }

    setExceptionDate(undefined);
    setExceptionSlots([]);

  };

  const removeException = (date: string) => {
    setScheduleData(prev => ({
      ...prev,
      exceptions: prev.exceptions.filter(e => e.date !== date),
    }));
  };

  const addExceptionSlot = () => {
    if (!newExceptionSlot) return;

    if (exceptionSlots.includes(newExceptionSlot)) {
      toast.error("Duplicate time slot for the same day is not allowed");
      return;
    }


    setExceptionSlots(prev => [...prev, newExceptionSlot].sort());
    setNewExceptionSlot("");
  };

  const listingActiveFilterCount = useMemo(() => {
    let count = 0;
    if (listingFilters.activeStatus !== "all") count++;
    if (listingFilters.priceMin || listingFilters.priceMax) count++;
    if (listingFilters.duration !== "all") count++;
    return count;
  }, [listingFilters]);

  const clearListingFilters = () => setListingFilters({
    activeStatus: "all",
    priceMin: "",
    priceMax: "",
    duration: "all",
  });

  const parseDurationMinutes = (dur: string): number => {
    const hrMatch = dur.match(/(\d+)\s*hr/i);
    const minMatch = dur.match(/(\d+)\s*min/i);
    return ((hrMatch ? parseInt(hrMatch[1]) : 0) * 60) + (minMatch ? parseInt(minMatch[1]) : 0);
  };

  const filteredListings = useMemo(() => {
    let result = listings;

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((l) =>
        l.name.toLowerCase().includes(q) ||
        (l.description && l.description.toLowerCase().includes(q))
      );
    }

    // Active status filter
    if (listingFilters.activeStatus !== "all") {
      result = result.filter((l) =>
        listingFilters.activeStatus === "active" ? l.is_active : !l.is_active
      );
    }

    // Price range filter
    if (listingFilters.priceMin || listingFilters.priceMax) {
      const min = listingFilters.priceMin ? parseFloat(listingFilters.priceMin) : 0;
      const max = listingFilters.priceMax ? parseFloat(listingFilters.priceMax) : Infinity;
      result = result.filter((l) => l.price >= min && l.price <= max);
    }

    // Duration filter
    if (listingFilters.duration !== "all") {
      result = result.filter((l) => {
        const mins = parseDurationMinutes(l.duration);
        switch (listingFilters.duration) {
          case "under_30": return mins > 0 && mins < 30;
          case "30_60": return mins >= 30 && mins <= 60;
          case "60_120": return mins > 60 && mins <= 120;
          case "120_plus": return mins > 120;
          default: return true;
        }
      });
    }

    return result;
  }, [listings, searchQuery, listingFilters]);

  const loading = outletLoading || (queryLoading && !!businessId && !!activeOutlet);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <AdminTabActionBar className="gap-2">
        {outlets.length > 0 && listings.length > 0 && (
          <>
              <div className="relative w-full max-w-[300px]">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search listings..."
                  className="admin-toolbar-input pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Popover open={listingFilterOpen} onOpenChange={setListingFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="admin-toolbar-button shrink-0 gap-1.5">
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    Filter
                    {listingActiveFilterCount > 0 && (
                      <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                        {listingActiveFilterCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Filters</h4>
                      {listingActiveFilterCount > 0 && (
                        <button onClick={clearListingFilters} className="text-xs text-primary hover:underline">
                          Clear all
                        </button>
                      )}
                    </div>

                    {/* Active Status */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Status</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(["all", "active", "inactive"] as const).map((opt) => (
                          <button
                            key={opt}
                            onClick={() => setListingFilters((prev) => ({ ...prev, activeStatus: opt }))}
                            className={cn(
                              "px-2.5 py-1 rounded-md text-xs font-medium transition-colors capitalize",
                              listingFilters.activeStatus === opt
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                            )}
                          >
                            {opt === "all" ? "All" : opt === "active" ? "Active only" : "Inactive only"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Price Range */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Price range</p>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          placeholder="Min"
                          className="h-8 text-sm"
                          value={listingFilters.priceMin}
                          onChange={(e) => setListingFilters((prev) => ({ ...prev, priceMin: e.target.value }))}
                        />
                        <span className="text-muted-foreground text-xs">—</span>
                        <Input
                          type="number"
                          placeholder="Max"
                          className="h-8 text-sm"
                          value={listingFilters.priceMax}
                          onChange={(e) => setListingFilters((prev) => ({ ...prev, priceMax: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Duration filter */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Duration</p>
                      <div className="flex flex-wrap gap-1.5">
                        {([
                          { value: "all" as const, label: "All" },
                          { value: "under_30" as const, label: "Under 30 min" },
                          { value: "30_60" as const, label: "30 – 60 min" },
                          { value: "60_120" as const, label: "1 – 2 hr" },
                          { value: "120_plus" as const, label: "2+ hr" },
                        ]).map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setListingFilters((prev) => ({ ...prev, duration: opt.value }))}
                            className={cn(
                              "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                              listingFilters.duration === opt.value
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          {onNavigateToPricing && (
            <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2.5" onClick={onNavigateToPricing}>
              <TrendingUp className="h-3.5 w-3.5" />
              Dynamic Pricing
            </Button>
          )}

          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <Button
              size="sm"
              className="h-7 gap-1.5 px-2.5"
              onClick={(e) => {
                e.preventDefault();
                if (listingLimit !== null && totalListingsCount >= listingLimit) {
                  setUpgradeModalOpen(true);
                } else {
                  setIsDialogOpen(true);
                }
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Add listing
            </Button>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingListing ? "Edit listing" : "Add listing"}</DialogTitle>
                <DialogDescription>
                  {editingListing ? "Make changes to the listing's details here." : "Add a new service listing to your storefront here."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <Tabs value={String(formStep)} onValueChange={(val) => setFormStep(Number(val) as 1 | 2 | 3)} className="w-full">
                  <TabsList className="bg-transparent border-b border-border p-0 w-full justify-start rounded-none h-auto gap-6 mb-6">
                    <TabsTrigger
                      value="1"
                      className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all"
                    >
                      Basic info
                    </TabsTrigger>
                    <TabsTrigger
                      value="2"
                      className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all"
                    >
                      Schedule
                    </TabsTrigger>
                    <TabsTrigger
                      value="3"
                      className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all"
                    >
                      Extras
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="1" className="space-y-6 mt-0 border-0 p-0 outline-none">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="listing-name">Name</Label>
                    <Input
                      id="listing-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="listing-description">Description</Label>
                    <Textarea
                      id="listing-description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe this service..."
                      rows={3}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="listing-teacher">Teacher <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input
                      id="listing-teacher"
                      value={formData.teacher_name}
                      onChange={(e) => setFormData({ ...formData, teacher_name: e.target.value })}
                      placeholder="e.g. Ms Priya"
                    />
                    <p className="text-xs text-muted-foreground">Shown to parents on this class and used by your AI assistant.</p>
                  </div>

                  {/* Phase C2 — attendee capture toggle. When on, the public
                      booking form asks "who's attending?" (child + level) and
                      links a student to the booking for attendance/make-ups. */}
                  <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <Label htmlFor="listing-collect-attendee">Ask who's attending</Label>
                      <p className="text-xs text-muted-foreground">
                        Collect the student's name &amp; level at booking, so attendance and make-ups track the child — not just the payer.
                      </p>
                    </div>
                    <Switch
                      id="listing-collect-attendee"
                      checked={formData.collect_attendee}
                      onCheckedChange={(v) => setFormData({ ...formData, collect_attendee: v })}
                    />
                  </div>

                  {/* Listing Photos */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <Label>Listing photos</Label>
                      <span className="text-xs text-muted-foreground">{formData.images.length} / {MAX_LISTING_IMAGES} photos</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {Array.from({ length: MAX_LISTING_IMAGES }).map((_, index) => {
                        const imageUrl = formData.images[index];
                        const isUploading = uploadingImageIndex === index;
                        return (
                          <div
                            key={index}
                            className="relative aspect-square rounded-xl border-2 border-dashed border-border overflow-hidden bg-card group/img transition-colors hover:border-primary/40"
                          >
                            {isUploading ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                              </div>
                            ) : imageUrl ? (
                              <>
                                <img
                                  src={imageUrl}
                                  alt={`Listing photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                {/* Hover overlay with actions */}
                                <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover/img:opacity-100">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      imageInputRefs.current[index]?.click();
                                    }}
                                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-foreground transition-colors"
                                    title="Replace image"
                                  >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleImageDelete(index);
                                    }}
                                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-destructive transition-colors"
                                    title="Delete image"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => imageInputRefs.current[index]?.click()}
                                disabled={index > formData.images.length}
                                className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <Upload className="w-5 h-5" />
                                <span className="text-[10px] font-medium">Upload</span>
                              </button>
                            )}
                            <input
                              ref={el => { imageInputRefs.current[index] = el; }}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleImageSelect(file, index);
                                e.target.value = "";
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">Supports: PNG, JPEG, JPG, WebP. You'll frame each photo to a square before it's added. The first image will be used as the cover photo.</p>
                  </div>

                  <ImageCropDialog
                    file={pendingCrop?.file ?? null}
                    onCancel={() => setPendingCrop(null)}
                    onCropComplete={handleCropComplete}
                  />

                  {outlets.length > 0 && (
                    <div className="space-y-1.5">
                      <Label htmlFor="listing-outlet">Outlet location</Label>
                      <Select
                        value={formData.outlet_id}
                        onValueChange={(value) => setFormData({ ...formData, outlet_id: value })}
                      >
                        <SelectTrigger id="listing-outlet">
                          <SelectValue placeholder="Select an outlet" />
                        </SelectTrigger>
                        <SelectContent>
                          {outlets.map((outlet) => (
                            <SelectItem key={outlet.outlet_id} value={outlet.outlet_id}>
                              {outlet.outlet_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Booking type — Session (group classes, fixed slots) vs
                      Appointment (a specific staff member for a set duration).
                      Hidden in the class-based vertical: every listing is a session. */}
                  {APPOINTMENT_BOOKING_ENABLED && (
                  <div className="space-y-1.5">
                    <Label>Booking type</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        { value: "session", title: "Session", desc: "Group classes at fixed times" },
                        { value: "appointment", title: "By appointment", desc: "A staff member for a set duration" },
                      ] as const).map((opt) => {
                        const selected = formData.booking_mode === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, booking_mode: opt.value })}
                            className={`text-left border rounded-xl px-3 py-2.5 transition-colors ${selected ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-input hover:border-muted-foreground/40"}`}
                          >
                            <div className="text-sm font-medium">{opt.title}</div>
                            <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{opt.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                    {formData.booking_mode === "appointment" && (
                      <p className="text-[11px] text-muted-foreground">
                        Times come from each staff member's working hours (set under <span className="font-medium">Staff</span>), not a weekly slot grid. Pay-at-venue only for now.
                      </p>
                    )}
                  </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="listing-duration">Duration</Label>
                      <div className="relative">
                        <Input
                          id="listing-duration"
                          type="number"
                          value={formData.duration.replace(/[^0-9]/g, '')}
                          onChange={(e) => setFormData({ ...formData, duration: e.target.value ? `${e.target.value} min` : '' })}
                          placeholder="e.g. 60"
                          className="pr-12"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">min</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="listing-price">Price ($)</Label>
                      <Input
                        id="listing-price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                    {formData.booking_mode === "appointment" ? (
                      <div className="space-y-1.5">
                        <Label htmlFor="listing-buffer">Buffer / turnaround</Label>
                        <div className="relative">
                          <Input
                            id="listing-buffer"
                            type="number"
                            min={0}
                            value={formData.buffer_minutes}
                            onChange={(e) => setFormData({ ...formData, buffer_minutes: parseInt(e.target.value) || 0 })}
                            placeholder="e.g. 10"
                            className="pr-12"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">min</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label htmlFor="listing-capacity">Maximum capacity</Label>
                        <Input
                          id="listing-capacity"
                          type="number"
                          value={formData.spots}
                          onChange={(e) => setFormData({ ...formData, spots: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                    )}
                  </div>

                  {/* How a recurring booking (programme/term) on this listing is
                      billed. Only relevant for session listings. */}
                  {formData.booking_mode === "session" && (
                    <div className="space-y-1.5 pt-1">
                      <Label>Recurring billing</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {([
                          { value: "upfront", title: "Pay upfront", desc: "Whole programme charged at booking" },
                          { value: "per_session", title: "Pay weekly", desc: "Each session charged 24h before" },
                        ] as const).map((opt) => {
                          const selected = formData.default_billing_mode === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, default_billing_mode: opt.value })}
                              className={`text-left border rounded-xl px-3 py-2.5 transition-colors ${selected ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-input hover:border-muted-foreground/40"}`}
                            >
                              <div className="text-sm font-medium">{opt.title}</div>
                              <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{opt.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Applies when a customer books this as a multi-week series. One-off bookings are unaffected.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tags / Additional Information */}
                <div className="border-t pt-6">
                  <Label className="mb-4 block">Additional information</Label>
                  <div className="space-y-2">
                    {[
                      { value: "instant_confirmation", label: "Instant confirmation", icon: Zap },
                      { value: "parking_available", label: "Parking available", icon: Car },
                      { value: "near_public_transport", label: "Near public transport", icon: TrainFront },
                    ].map((tag) => (
                      <label
                        key={tag.value}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer bg-card hover:bg-card transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.tags.includes(tag.value)}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              tags: e.target.checked
                                ? [...prev.tags, tag.value]
                                : prev.tags.filter(t => t !== tag.value),
                            }));
                          }}
                          className="sr-only"
                        />
                        <div className={cn(
                          "h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center transition-colors",
                          formData.tags.includes(tag.value)
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-muted-foreground/30"
                        )}>
                          {formData.tags.includes(tag.value) && <Check className="h-3 w-3" />}
                        </div>
                        <tag.icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{tag.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                </TabsContent>

                <TabsContent value="2" className="space-y-6 mt-0 border-0 p-0 outline-none">
                {formData.booking_mode === "appointment" ? (
                  <div className="pt-2">
                    <div className="border rounded-xl p-5 bg-muted/30 space-y-2">
                      <p className="text-sm font-medium">Availability comes from your staff's hours</p>
                      <p className="text-sm text-muted-foreground">
                        Appointment services don't use a weekly slot grid. Open times are worked out from each staff member's
                        working hours (set under the <span className="font-medium">Staff</span> tab) minus their existing bookings and
                        the duration + buffer you set. There's no slot grid to set up.
                      </p>
                    </div>
                  </div>
                ) : (
                <>
                {/* Schedule Type */}
                <div className="pt-2">
                  <Label className="mb-4 block">Schedule type</Label>
                  <div className="flex gap-4">
                    <label className={cn(
                      "flex-1 flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-colors",
                      scheduleData.type !== 'one_off' ? "border-primary bg-primary/5" : "border-muted bg-card hover:bg-card"
                    )}>
                      <input
                        type="radio"
                        name="scheduleType"
                        className="sr-only"
                        checked={scheduleData.type !== 'one_off'}
                        onChange={() => setScheduleData(prev => ({ ...prev, type: 'recurring' }))}
                      />
                      <CalendarDays className="w-6 h-6 mb-2 text-primary" />
                      <span className="font-medium text-sm">Recurring Weekly</span>
                      <span className="text-xs text-muted-foreground mt-1 text-center">Repeats every week</span>
                    </label>
                    <label className={cn(
                      "flex-1 flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-colors",
                      scheduleData.type === 'one_off' ? "border-primary bg-primary/5" : "border-muted bg-card hover:bg-card"
                    )}>
                      <input
                        type="radio"
                        name="scheduleType"
                        className="sr-only"
                        checked={scheduleData.type === 'one_off'}
                        onChange={() => setScheduleData(prev => ({ ...prev, type: 'one_off' }))}
                      />
                      <CalendarDays className="w-6 h-6 mb-2 text-primary" />
                      <span className="font-medium text-sm">One-off Event</span>
                      <span className="text-xs text-muted-foreground mt-1 text-center">Specific dates only</span>
                    </label>
                  </div>
                </div>

                {/* Weekly Schedule */}
                {scheduleData.type !== 'one_off' && (
                  <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <Label>Weekly time slots</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDuplicateModalOpen(true)}
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" />
                        Duplicate day
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setBulkModalOpen(true)}
                      >
                        <CalendarDays className="w-3.5 h-3.5 mr-1" />
                        Bulk create time slots
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {DAYS_OF_WEEK.map(day => (
                      <div
                        key={day}
                        className="flex items-start gap-4 py-3 px-4 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="w-24 text-sm font-medium pt-1.5">
                          {DAY_LABELS[day]}
                        </div>
                        <div className="flex-1 flex flex-wrap items-center gap-2 min-h-[2rem]">
                          {scheduleData.weekly[day]?.length > 0 ? (
                            scheduleData.weekly[day].map(time => (
                              <span
                                key={time}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-md text-sm font-medium"
                              >
                                {formatTimeTo12h(time)}
                                <button
                                  type="button"
                                  onClick={() => removeTimeSlot(day, time)}
                                  className="hover:text-primary/70 transition-colors"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">No slots</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (!newTimeSlot[day]) setNewTimeSlot(prev => ({ ...prev, [day]: "09:00" }));
                              setTimePickerDay(day);
                            }}
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Add time slot
                          </Button>
                          <ScrollTimePicker
                            open={timePickerDay === day}
                            onOpenChange={(open) => { if (!open) setTimePickerDay(null); }}
                            value={newTimeSlot[day] || "09:00"}
                            onSave={(val) => {
                              if (scheduleData.weekly[day]?.includes(val)) {
                                toast.error("Duplicate time slot for the same day is not allowed");
                                return;
                              }
                              if (parseInt(val.split(":")[1], 10) % 5 !== 0) {
                                toast.error("Time slots must be in increments of 5 minutes");
                                return;
                              }
                              setNewTimeSlot(prev => ({ ...prev, [day]: val }));
                              // Directly add the slot
                              setScheduleData(prev => ({
                                ...prev,
                                weekly: {
                                  ...prev.weekly,
                                  [day]: [...(prev.weekly[day] || []), val].sort(),
                                },
                              }));
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <BulkCreateSlotsModal
                    open={bulkModalOpen}
                    onOpenChange={setBulkModalOpen}
                    listingDuration={formData.duration}
                    currentWeeklySchedule={scheduleData.weekly}
                    durationMode="read-only"
                    onApply={(day, newSlots) => {
                      setScheduleData(prev => ({
                        ...prev,
                        weekly: {
                          ...prev.weekly,
                          [day]: newSlots,
                        },
                      }));
                    }}
                  />
                  <DuplicateDaySlotsModal
                    open={duplicateModalOpen}
                    onOpenChange={setDuplicateModalOpen}
                    currentWeeklySchedule={scheduleData.weekly}
                    onApply={(targetDay, newSlots) => {
                      setScheduleData(prev => ({
                        ...prev,
                        weekly: {
                          ...prev.weekly,
                          [targetDay]: newSlots,
                        },
                      }));
                    }}
                  />
                </div>
                )}

                {/* Date Exceptions */}
                <div className="border-t pt-6">
                  <Label className="mb-4 block">{scheduleData.type === 'one_off' ? 'Event dates' : 'Date exceptions'}</Label>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Calendar */}
                    <div className="border rounded-xl overflow-hidden">
                      {scheduleData.type === 'one_off' ? (
                        <Calendar
                          mode="multiple"
                          selected={scheduleData.exceptions.map(e => new Date(e.date))}
                          onSelect={(dates) => {
                            const selectedDates = (dates as Date[]) || [];
                            const selectedDateStrs = selectedDates.map(d => format(d, "yyyy-MM-dd"));
                            
                            const newExceptions = scheduleData.exceptions.filter((e: any) => selectedDateStrs.includes(e.date));
                            const existingDateStrs = newExceptions.map(e => e.date);
                            
                            selectedDateStrs.forEach(dateStr => {
                              if (!existingDateStrs.includes(dateStr)) {
                                newExceptions.push({ date: dateStr, slots: [], isClosed: false });
                              }
                            });
                            
                            newExceptions.sort((a, b) => a.date.localeCompare(b.date));
                            setScheduleData(prev => ({ ...prev, exceptions: newExceptions }));
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          className={cn("p-3 pointer-events-auto w-full")}
                          classNames={{
                            months: "flex flex-col space-y-4",
                            month: "space-y-4 w-full",
                            caption: "flex justify-center pt-1 relative items-center",
                            caption_label: "text-sm font-medium",
                            nav: "space-x-1 flex items-center",
                            nav_button: cn(
                              "h-8 w-8 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-full hover:bg-accent hover:text-accent-foreground transition-colors"
                            ),
                            nav_button_previous: "absolute left-1",
                            nav_button_next: "absolute right-1",
                            table: "w-full border-collapse",
                            head_row: "flex w-full",
                            head_cell: "text-muted-foreground rounded-md w-full font-normal text-[0.8rem] flex-1 text-center",
                            row: "flex w-full mt-1",
                            cell: cn(
                              "relative flex-1 p-0.5 text-center text-sm focus-within:relative focus-within:z-20"
                            ),
                            day: cn(
                              "h-9 w-9 mx-auto p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md inline-flex items-center justify-center transition-colors"
                            ),
                            day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                            day_today: "border-2 border-primary text-primary font-medium",
                            day_outside: "text-muted-foreground opacity-50",
                            day_disabled: "text-muted-foreground opacity-30",
                            day_hidden: "invisible",
                          }}
                        />
                      ) : (
                        <Calendar
                          mode="single"
                          selected={exceptionDate}
                          onSelect={(date) => {
                            setExceptionDate(date);
                            if (date) {
                              const dateStr = format(date, "yyyy-MM-dd");
                              const existing = scheduleData.exceptions.find(e => e.date === dateStr);
                              setExceptionSlots(existing ? [...existing.slots] : []);
                            } else {
                              setExceptionSlots([]);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          className={cn("p-3 pointer-events-auto w-full")}
                          classNames={{
                            months: "flex flex-col space-y-4",
                            month: "space-y-4 w-full",
                            caption: "flex justify-center pt-1 relative items-center",
                            caption_label: "text-sm font-medium",
                            nav: "space-x-1 flex items-center",
                            nav_button: cn(
                              "h-8 w-8 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-full hover:bg-accent hover:text-accent-foreground transition-colors"
                            ),
                            nav_button_previous: "absolute left-1",
                            nav_button_next: "absolute right-1",
                            table: "w-full border-collapse",
                            head_row: "flex w-full",
                            head_cell: "text-muted-foreground rounded-md w-full font-normal text-[0.8rem] flex-1 text-center",
                            row: "flex w-full mt-1",
                            cell: cn(
                              "relative flex-1 p-0.5 text-center text-sm focus-within:relative focus-within:z-20"
                            ),
                            day: cn(
                              "h-9 w-9 mx-auto p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md inline-flex items-center justify-center transition-colors"
                            ),
                            day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                            day_today: "border-2 border-primary text-primary font-medium",
                            day_outside: "text-muted-foreground opacity-50",
                            day_disabled: "text-muted-foreground opacity-30",
                            day_hidden: "invisible",
                          }}
                        />
                      )}
                    </div>

                    {/* Exception Details */}
                    <div className="space-y-4">
                      {scheduleData.type === 'one_off' ? (
                        <div className="space-y-4">
                          {scheduleData.exceptions.length > 0 ? (
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground">Select timeslots for each event date. Dates without timeslots will not be bookable.</p>
                              <div className="max-h-[400px] overflow-y-auto pr-2 space-y-4">
                                {scheduleData.exceptions.map(exception => (
                                  <div key={exception.date} className="p-4 border rounded-xl bg-muted/10 space-y-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <CalendarDays className="w-4 h-4 text-primary" />
                                        <span className="font-medium">{format(new Date(exception.date), "EEEE, MMMM d, yyyy")}</span>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                        onClick={() => {
                                          setScheduleData(prev => ({
                                            ...prev,
                                            exceptions: prev.exceptions.filter(e => e.date !== exception.date)
                                          }));
                                        }}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                    
                                    <div className="flex flex-wrap gap-2">
                                      {exception.slots.map(time => (
                                        <span key={time} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-md text-sm font-medium">
                                          {formatTimeTo12h(time)}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setScheduleData(prev => ({
                                                ...prev,
                                                exceptions: prev.exceptions.map(e => e.date === exception.date ? { ...e, slots: e.slots.filter(t => t !== time) } : e)
                                              }));
                                            }}
                                            className="hover:text-primary/70"
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                    <div className="flex items-center gap-2 pt-1">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          if (!newTimeSlot[exception.date]) setNewTimeSlot(prev => ({ ...prev, [exception.date]: "09:00" }));
                                          setTimePickerDay(exception.date);
                                        }}
                                      >
                                        <Plus className="w-3.5 h-3.5 mr-1" />
                                        Add time slot
                                      </Button>
                                      <ScrollTimePicker
                                        open={timePickerDay === exception.date}
                                        onOpenChange={(open) => { if (!open) setTimePickerDay(null); }}
                                        value={newTimeSlot[exception.date] || "09:00"}
                                        onSave={(val) => {
                                          if (exception.slots.includes(val)) {
                                            toast.error("Duplicate time slot for the same day is not allowed");
                                            return;
                                          }
                                          if (parseInt(val.split(":")[1], 10) % 5 !== 0) {
                                            toast.error("Time slots must be in increments of 5 minutes");
                                            return;
                                          }
                                          setNewTimeSlot(prev => ({ ...prev, [exception.date]: val }));
                                          setScheduleData(prev => ({
                                            ...prev,
                                            exceptions: prev.exceptions.map(e => e.date === exception.date ? { ...e, slots: [...e.slots, val].sort() } : e)
                                          }));
                                        }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full py-8 text-center text-muted-foreground border rounded-xl bg-muted/10 border-dashed">
                              <CalendarDays className="w-10 h-10 mb-3 opacity-50" />
                              <p className="text-sm">Select dates on the calendar to configure event times</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {exceptionDate ? (
                            <div className="space-y-4">
                              <div className="flex items-center gap-3">
                                <CalendarDays className="w-5 h-5 text-primary" />
                                <span className="font-medium">{format(exceptionDate, "EEEE, MMMM d, yyyy")}</span>
                              </div>

                              <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">If no time slots are chosen, the store will be closed on this day.</p>
                                <div className="flex flex-wrap gap-2">
                                  {exceptionSlots.map(time => (
                                    <span
                                      key={time}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-md text-sm font-medium"
                                    >
                                      {formatTimeTo12h(time)}
                                      <button
                                        type="button"
                                        onClick={() => setExceptionSlots(prev => prev.filter(t => t !== time))}
                                        className="hover:text-primary/70"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      if (!newExceptionSlot) setNewExceptionSlot("09:00");
                                      setExceptionTimePickerOpen(true);
                                    }}
                                  >
                                    <Plus className="w-3.5 h-3.5 mr-1" />
                                    Add time slot
                                  </Button>
                                  <ScrollTimePicker
                                    open={exceptionTimePickerOpen}
                                    onOpenChange={setExceptionTimePickerOpen}
                                    value={newExceptionSlot || "09:00"}
                                    onSave={(val) => {
                                      if (exceptionSlots.includes(val)) {
                                        toast.error("Duplicate time slot for the same day is not allowed");
                                        return;
                                      }
                                      if (parseInt(val.split(":")[1], 10) % 5 !== 0) {
                                        toast.error("Time slots must be in increments of 5 minutes");
                                        return;
                                      }
                                      setNewExceptionSlot(val);
                                      setExceptionSlots(prev => [...prev, val].sort());
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    onClick={addException}
                                    size="sm"
                                  >
                                    Save exception
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full py-8 text-center text-muted-foreground">
                              <CalendarDays className="w-10 h-10 mb-3 opacity-50" />
                              <p className="text-sm">Select a date to add an exception</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Existing exceptions */}
                  {scheduleData.type !== 'one_off' && scheduleData.exceptions.length > 0 && (
                    <div className="mt-6 space-y-2">
                      <Label className="text-sm text-muted-foreground">Saved exceptions</Label>
                      <div className="space-y-2">
                        {scheduleData.exceptions.map(exception => (
                          <div
                            key={exception.date}
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3 flex-wrap">
                              <CalendarDays className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{format(new Date(exception.date), "d MMM yyyy (EEE)")}</span>
                              {exception.isClosed ? (
                                <span className="px-2 py-0.5 bg-destructive/10 text-destructive text-xs rounded-full font-medium">Closed</span>
                              ) : (
                                <div className="flex flex-wrap gap-1.5">
                                  {exception.slots.map(s => (
                                    <span key={s} className="inline-flex items-center px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 rounded-md text-xs font-medium">
                                      {formatTimeTo12h(s)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeException(exception.date)}
                              className="h-8 w-8"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                </>
                )}

                {/* Booking cut-off: online bookings stop N hours before a session. */}
                <div className="space-y-1.5 border-t pt-6">
                  <Label htmlFor="listing-booking-cutoff">
                    Stop online bookings <span className="font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <div className="relative max-w-[240px]">
                    <Input
                      id="listing-booking-cutoff"
                      type="number"
                      min={1}
                      max={720}
                      inputMode="numeric"
                      value={formData.booking_cutoff_hours ?? ""}
                      onChange={(e) => {
                        const hours = parseInt(e.target.value, 10);
                        setFormData({
                          ...formData,
                          booking_cutoff_hours: Number.isFinite(hours) && hours > 0 ? Math.min(hours, 720) : null,
                        });
                      }}
                      placeholder="e.g. 24"
                      className="pr-28"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                      hours before
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Customers can't book or move a booking online within this many hours of a session starting. Leave blank to take bookings until it starts. You can still add bookings yourself.
                  </p>
                </div>
                </TabsContent>

                <TabsContent value="3" className="space-y-6 mt-0 border-0 p-0 outline-none">
                {/* Custom Booking Fields Section */}
                <div className="pt-2 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Custom booking fields</Label>
                      <p className="text-xs text-muted-foreground mt-1">Collect additional information from customers during booking. Max 5 fields.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        custom_questions: [...prev.custom_questions, { id: crypto.randomUUID(), question: "", type: "text", required: false }] 
                      }))}
                      disabled={formData.custom_questions.length >= 5}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Field
                    </Button>
                  </div>
                  
                  {formData.custom_questions.length > 0 ? (
                    <div className="space-y-4">
                      {formData.custom_questions.map((q, index) => (
                        <div key={q.id || index} className="flex gap-3 items-start border rounded-xl p-4 bg-muted/20">
                          <div className="flex-1 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Question</Label>
                                <Input
                                  value={q.question}
                                  onChange={(e) => {
                                    const newQs = [...formData.custom_questions];
                                    newQs[index].question = e.target.value;
                                    setFormData(prev => ({ ...prev, custom_questions: newQs }));
                                  }}
                                  placeholder="e.g., What is your experience level?"
                                  className="bg-background"
                                  required
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">Field Type</Label>
                                <Select
                                  value={q.type}
                                  onValueChange={(val: "text" | "dropdown" | "radio") => {
                                    const newQs = [...formData.custom_questions];
                                    newQs[index].type = val;
                                    if ((val === "dropdown" || val === "radio") && (!newQs[index].options || newQs[index].options.length === 0)) {
                                      newQs[index].options = ["Option 1", "Option 2"];
                                    }
                                    setFormData(prev => ({ ...prev, custom_questions: newQs }));
                                  }}
                                >
                                  <SelectTrigger className="bg-background">
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="text">Short Answer (Text)</SelectItem>
                                    <SelectItem value="dropdown">Dropdown Menu</SelectItem>
                                    <SelectItem value="radio">Multiple Choice (Radio Buttons)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            {(q.type === "dropdown" || q.type === "radio") && (
                              <div>
                                <Label className="text-xs text-muted-foreground mb-2 block">Options</Label>
                                <div className="space-y-2">
                                  {(q.options || []).map((opt, optIndex) => (
                                    <div key={optIndex} className="flex items-center gap-2">
                                      <Input
                                        value={opt}
                                        onChange={(e) => {
                                          const newQs = [...formData.custom_questions];
                                          const newOptions = [...(newQs[index].options || [])];
                                          newOptions[optIndex] = e.target.value;
                                          newQs[index].options = newOptions;
                                          setFormData(prev => ({ ...prev, custom_questions: newQs }));
                                        }}
                                        placeholder={`Option ${optIndex + 1}`}
                                        className="bg-background"
                                        required
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="shrink-0 h-9 w-9 text-muted-foreground hover:text-destructive"
                                        onClick={() => {
                                          const newQs = [...formData.custom_questions];
                                          const newOptions = [...(newQs[index].options || [])];
                                          newOptions.splice(optIndex, 1);
                                          newQs[index].options = newOptions;
                                          setFormData(prev => ({ ...prev, custom_questions: newQs }));
                                        }}
                                        disabled={(q.options || []).length <= 1}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="mt-2 text-xs"
                                  onClick={() => {
                                    const newQs = [...formData.custom_questions];
                                    const newOptions = [...(newQs[index].options || [])];
                                    newOptions.push(`Option ${newOptions.length + 1}`);
                                    newQs[index].options = newOptions;
                                    setFormData(prev => ({ ...prev, custom_questions: newQs }));
                                  }}
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Add Option
                                </Button>
                              </div>
                            )}

                            <div className="flex items-center gap-2">
                              <Switch
                                checked={q.required}
                                onCheckedChange={(checked) => {
                                  const newQs = [...formData.custom_questions];
                                  newQs[index].required = checked;
                                  setFormData(prev => ({ ...prev, custom_questions: newQs }));
                                }}
                              />
                              <Label className="text-xs font-medium cursor-pointer" onClick={() => {
                                  const newQs = [...formData.custom_questions];
                                  newQs[index].required = !newQs[index].required;
                                  setFormData(prev => ({ ...prev, custom_questions: newQs }));
                                }}>Required Field</Label>
                            </div>
                          </div>
                          
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive shrink-0 mt-6"
                            onClick={() => {
                              const newQs = formData.custom_questions.filter((_, i) => i !== index);
                              setFormData(prev => ({ ...prev, custom_questions: newQs }));
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed rounded-xl p-8 text-center bg-muted/20">
                      <p className="text-sm text-muted-foreground">No custom fields added.</p>
                      <p className="text-xs text-muted-foreground mt-1">Optional: Collect more information when customers book.</p>
                    </div>
                  )}
                </div>

                {/* FAQ Section */}
                <div className="border-t pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Frequently asked questions</Label>
                      <p className="text-xs text-muted-foreground mt-1">Add common questions and answers for this specific listing.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, faqs: [...prev.faqs, { question: "", answer: "" }] }))}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add FAQ
                    </Button>
                  </div>
                  
                  {formData.faqs.length > 0 ? (
                    <div className="space-y-4">
                      {formData.faqs.map((faq, index) => (
                        <div key={index} className="flex gap-3 items-start border rounded-xl p-4 bg-muted/20">
                          <div className="flex-1 space-y-3">
                            <div>
                              <Label className="text-xs text-muted-foreground mb-1 block">Question</Label>
                              <Input
                                value={faq.question}
                                onChange={(e) => {
                                  const newFaqs = [...formData.faqs];
                                  newFaqs[index].question = e.target.value;
                                  setFormData(prev => ({ ...prev, faqs: newFaqs }));
                                }}
                                placeholder="e.g., What should I bring?"
                                className="bg-background"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground mb-1 block">Answer</Label>
                              <Textarea
                                value={faq.answer}
                                onChange={(e) => {
                                  const newFaqs = [...formData.faqs];
                                  newFaqs[index].answer = e.target.value;
                                  setFormData(prev => ({ ...prev, faqs: newFaqs }));
                                }}
                                placeholder="Provide a helpful answer..."
                                rows={2}
                                className="bg-background resize-none"
                              />
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive shrink-0 mt-6"
                            onClick={() => {
                              const newFaqs = formData.faqs.filter((_, i) => i !== index);
                              setFormData(prev => ({ ...prev, faqs: newFaqs }));
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed rounded-xl p-8 text-center bg-muted/20">
                      <p className="text-sm text-muted-foreground">No FAQs added yet.</p>
                      <p className="text-xs text-muted-foreground mt-1">Help your customers by answering common questions upfront.</p>
                    </div>
                  )}
                </div>

                {/* Terms and Conditions */}
                <div className="border-t pt-6">
                  <div className="space-y-1.5">
                    <Label htmlFor="listing-terms">Terms and conditions</Label>
                    <Textarea
                      id="listing-terms"
                      value={formData.terms_and_conditions}
                      onChange={(e) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
                      placeholder="Cancellation policy, refund terms, etc."
                      rows={3}
                    />
                  </div>
                </div>
                </TabsContent>
                </Tabs>

                <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6">
                  {formStep > 1 && (
                    <Button type="button" variant="outline" onClick={() => setFormStep(prev => prev - 1 as 1 | 2 | 3)}>
                      Back
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  {formStep < 3 ? (
                    <Button key="next-btn" type="button" onClick={(e) => {
                      e.preventDefault();
                      setFormStep(prev => prev + 1 as 1 | 2 | 3);
                    }}>
                      Next
                    </Button>
                  ) : (
                    <Button key="submit-btn" type="submit">
                      {editingListing ? "Save changes" : "Create"}
                    </Button>
                  )}
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </AdminTabActionBar>
      {outlets.length === 0 ? (
          <p className="text-muted-foreground text-center py-12">Create an outlet to start adding listings.</p>
        ) : listings.length === 0 ? (
          <p className="text-muted-foreground text-center py-12">You have no listings yet. Add one to display it on your storefront.</p>
        ) : (
          <div className="space-y-4">

            <div className="admin-data-table-shell">
            <div className="admin-data-table-scroll w-full overflow-x-auto bg-card">
            <Table className="admin-data-table" style={{ minWidth: 900 }}>
              <TableHeader className="admin-data-table-header">
                <TableRow className="admin-data-table-header-row">
                  <TableHead style={{ width: 90 }}>Active</TableHead>
                  <TableHead style={{ width: 260 }}>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead style={{ width: 120 }}>Duration</TableHead>
                  <TableHead style={{ width: 120 }}>Base Price</TableHead>
                  <TableHead style={{ width: 48 }}></TableHead>
                </TableRow>
              </TableHeader>
              <TooltipProvider delayDuration={200}><TableBody className="admin-data-table-body">
                {filteredListings.length === 0 ? (
                  <TableRow className="admin-data-table-row">
                    <TableCell colSpan={6} className="admin-data-table-cell h-24 text-center text-muted-foreground">
                      No listings found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredListings.map((listing) => (
                    <TableRow key={listing.listing_id} className="group admin-data-table-row">
                      <TableCell className="admin-data-table-cell">
                        <Switch
                          checked={listing.is_active}
                          onCheckedChange={() => handleToggleClick(listing)}
                        />
                      </TableCell>
                      <TableCell className="admin-data-table-cell font-medium text-foreground"><CellText>{listing.name}</CellText></TableCell>
                      <TableCell className="admin-data-table-cell text-muted-foreground">
                        <CellText>{listing.description}</CellText>
                      </TableCell>
                      <TableCell className="admin-data-table-cell text-muted-foreground">{listing.duration}</TableCell>
                      <TableCell className="admin-data-table-cell text-muted-foreground tabular-nums">${listing.price}</TableCell>
                      <TableCell className="admin-data-table-cell">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                const protocol = window.location.protocol;
                                const host = window.location.hostname.replace(/^admin\./, "");
                                const port = window.location.port ? `:${window.location.port}` : "";
                                const url = `${protocol}//${host}${port}/listing/${listing.listing_id}`;
                                window.open(url, "_blank");
                              }}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(listing)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => openDeleteModal(listing)}
                              className="text-destructive focus:text-destructive focus:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody></TooltipProvider>
            </Table>
            </div>
            </div>

          </div>
        )}
        <div className="flex justify-end mt-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
            Showing Listing for {activeOutlet?.outlet_name || "Main Outlet"}
          </p>
        </div>

      {/* Custom Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteModalOpen(false);
          setListingToDelete(null);
          setActiveBookingCount(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Listing</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {checkingBookings ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking for active bookings…
                  </div>
                ) : activeBookingCount !== null && activeBookingCount > 0 ? (
                  <>
                    <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive font-medium">
                      <X className="w-4 h-4 shrink-0" />
                      This listing has {activeBookingCount} active booking{activeBookingCount === 1 ? "" : "s"}. It cannot be deleted until all bookings are cancelled or completed.
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {listingToDelete?.is_active ? (
                        <>You can <span className="font-medium text-foreground">archive</span> this listing instead — it will be hidden from your public booking page but all existing bookings will remain intact.</>
                      ) : (
                        <>This listing is already archived and hidden from your storefront. Once all active bookings are cancelled or completed, you will be able to delete it.</>
                      )}
                    </p>
                  </>
                ) : (
                  <p>
                    Are you sure you want to delete <span className="font-semibold text-foreground">{listingToDelete?.name}</span>? This action cannot be undone and will remove the listing from your public booking page.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            {activeBookingCount !== null && activeBookingCount > 0 ? (
              listingToDelete?.is_active ? (
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    if (listingToDelete) {
                      handleToggleActive({ ...listingToDelete, is_active: true } as Listing);
                      setDeleteModalOpen(false);
                      setListingToDelete(null);
                      setActiveBookingCount(null);
                      toast.success(`"${listingToDelete.name}" has been archived and hidden from your storefront.`);
                    }
                  }}
                  className="bg-muted text-foreground hover:bg-muted/80 border"
                >
                  Archive Instead
                </AlertDialogAction>
              ) : null
            ) : (
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete(listingToDelete?.listing_id);
                }}
                disabled={isDeleting || checkingBookings}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate Confirmation Modal */}
      <AlertDialog open={deactivateModalOpen} onOpenChange={(open) => {
        if (!open) {
          setDeactivateModalOpen(false);
          setListingToDeactivate(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Make listing inactive?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to make <span className="font-semibold text-foreground">{listingToDeactivate?.name}</span> inactive? This will hide the listing from your public booking page and customers will no longer be able to book it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (listingToDeactivate) {
                  handleToggleActive(listingToDeactivate);
                }
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Confirmation Modal */}
      <AlertDialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Plan Limit Reached</AlertDialogTitle>
            <AlertDialogDescription>
              You have hit the maximum number of listings ({listingLimit}) for your current plan. To create more listings, please upgrade your plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={(e) => {
                e.preventDefault();
                setUpgradeModalOpen(false);
                navigate("?tab=settings&settings=billing");
              }}
            >
              Upgrade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Price Change Warning Modal */}
      <AlertDialog open={priceChangeModalOpen} onOpenChange={(open) => {
        if (!open) handlePriceChangeCancel();
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Price change detected</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are changing the price from{" "}
                  <span className="font-semibold text-foreground">${editingListing?.price}</span>{" "}
                  to{" "}
                  <span className="font-semibold text-foreground">${formData.price}</span>.
                  Please note the following:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-sm">
                  <li>
                    <span className="font-medium text-foreground">Existing bookings</span> — Customers who have already booked will continue to pay the previous price of <span className="font-semibold text-foreground">${editingListing?.price}</span>.
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Pay at venue</span> — Customers who booked to pay at the venue before the price change will still pay the original price of <span className="font-semibold text-foreground">${editingListing?.price}</span> to maintain price integrity.
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Future online bookings</span> — New customers booking online will pay the updated price of <span className="font-semibold text-foreground">${formData.price}</span>.
                  </li>
                </ul>
                <p className="text-sm">Do you want to proceed with this price change?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handlePriceChangeCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => {
              e.preventDefault();
              handlePriceChangeConfirm();
            }}>
              Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StorefrontListings;
